import { documentPreviewMenuLabel } from "@/features/documents/lib/document-preview-view";
import { resolveDocumentOpenAction } from "@/features/documents/lib/document-row-view";
import type { DocumentPreviewKind } from "@/lib/file-types";
import { UNAVAILABLE_DOCUMENT_EVIDENCE_TITLE } from "./document-evidence-dom";
import type {
  DocumentCitationRow,
  EvidenceCitationRow,
  KnowledgeSourceCitationRow,
} from "./evidence-citations-popover";
import type { EvidenceTableRowRange } from "./evidence-excerpt";
import {
  excerptContainsTable,
  parseEvidenceTableRowRange,
  stripEvidenceTableRowAnnotation,
} from "./evidence-excerpt";
import { type EvidenceSourceIcon, evidenceFileIconKey } from "./evidence-source-icons";

/** Hands over the bytes of a file this app has no body for — the Documents row's own word. */
export const DOWNLOAD_EVIDENCE_FILE_LABEL = "Download";

/** Leaves for a web page: a link artifact's source is an address, not a file. */
export const OPEN_EVIDENCE_LINK_LABEL = "Open link";

/**
 * Opens the recording's transcript in place, at the cited turn. A recording's own bytes are
 * audio, so handing them to the browser shows nobody the quoted passage.
 */
export const VIEW_EVIDENCE_TRANSCRIPT_LABEL = "View transcript";

/** Opens the clarification thread the answer was settled in. */
export const OPEN_EVIDENCE_REQUEST_LABEL = "Open request";

/** What a hand-written knowledge entry is called where a file name would otherwise sit. */
export const MANUAL_EVIDENCE_TITLE = "Knowledge entry";

/**
 * The one line a document group draws when its source has been replaced since it was cited.
 *
 * A statement, not a warning: the quote below it is exactly what was read, out of a file this
 * popover still opens. Whether the document now says something else is a question nobody here
 * has asked, and the wording must not imply an answer to it.
 */
export const EVIDENCE_SOURCE_OUTDATED_NOTE = "Source updated since this was cited";

/** What the record kind is named on the location line of a clarification source. */
const CLARIFICATION_SOURCE_LABEL = "Clarification request";

/** One place a source was cited: where in it, and what was found there. */
export interface EvidencePlace {
  key: string;
  /** `Page 2 · Machine Settings`, the thread's attribution line, or nothing. */
  location: string | null;
  /** The quoted passage, or `null` while it loads and when it is gone. */
  excerpt: string | null;
  /** Quoted verbatim from a source file, so it is set in the same face the file is. */
  monospace: boolean;
  /** The quote renders a table, which is a shape of its own rather than quoted prose. */
  table: boolean;
  /** Which rows of a bigger table the quote holds, when the quote says so. */
  tableRows: EvidenceTableRowRange | null;
  figure?: DocumentCitationRow["figure"];
  /** Why the citing surface added this source, when it recorded a reason. */
  supportNote: string | null;
}

/**
 * The single action a source offers.
 *
 * A `link` leaves the page: the browser is handed the bytes of a file this app cannot draw, a
 * link artifact's web page, or the clarification thread's route. The other two stay on it. A
 * `transcript` opens the transcript viewer at `blockIndex` (`null` when the citation names a
 * revision the viewer no longer reads, so it opens at the top), because a recording's passage
 * is only readable there. A `preview` opens the Documents screen's own dialog on a body this
 * app draws itself.
 */
export type EvidenceSourceAction =
  | {
      kind: "link";
      label: string;
      /** Which of the two the anchor does: hand over bytes, or leave for a page. */
      icon: "download" | "external";
      href: string;
    }
  | {
      kind: "transcript";
      label: string;
      artifactId: string;
      name: string;
      /**
       * The transcription this passage was cited from, so the viewer opens the transcript the
       * quote is in. `null` for a source that names none — the document as it reads now.
       */
      processingRevisionId: string | null;
      blockIndex: number | null;
    }
  | {
      kind: "preview";
      label: string;
      previewKind: DocumentPreviewKind;
      artifactId: string;
      /** The name a person reads — the dialog's title. */
      name: string;
      /** The upload name — the extension the dialog's header states and the saved filename. */
      fileName: string;
      mimeType: string | null;
      /** The signed source URL — what the body draws and the footer hands over. */
      url: string;
    };

/**
 * One source, with every place it was cited underneath it. A document that a statement
 * rests on in three places is named once and carries one action; the three places follow
 * as location plus quote.
 */
export interface EvidenceSourceGroup {
  key: string;
  /** The file name, the thread title, or {@link MANUAL_EVIDENCE_TITLE}. */
  title: string;
  icon: EvidenceSourceIcon;
  /** The single action a source offers, or `null` when opening it means nothing. */
  action: EvidenceSourceAction | null;
  /** Who wrote it and when, shown where the action would be. Hand-written entries only. */
  meta: string | null;
  /**
   * The title and the places were written in chat — an `ask_user` exchange or a clarification
   * thread — where an element is referred to by a mention link and lists, links and line
   * breaks are the author's: they render as such, the mention as a chip. A quote from a file
   * and a knowledge entry typed into a plain box are verbatim and carry none of that.
   */
  references: boolean;
  /**
   * The document has been read again from a newer source since one of these places was cited.
   * Per group rather than per place: every place under one name was cited out of the same
   * capture, so one citation of a superseded one is enough to make it true of all of them.
   */
  sourceOutdated?: true;
  places: EvidencePlace[];
}

/**
 * Two citations of the same *capture* of a document belong to the same group, which is what
 * lets the group carry one action: a revision was parsed from one immutable source version, so
 * two revisions of a refreshed document are two files with two URLs, and one group spanning
 * both would open the first one's file for the second one's passages.
 *
 * A row with no revision — a server-resolved knowledge source, which means "the document as it
 * is now" — keys on the artifact alone, and the name is the last resort.
 *
 * A document cited on both sides of a refresh therefore names itself twice. The anatomy already
 * reads that: the group built on the superseded revision carries the source-updated line and its
 * own action, the one built on the current revision carries neither. A version marker beside the
 * name would be a second element in a fixed anatomy, saying what that line says already.
 */
function documentGroupKey(row: DocumentCitationRow): string {
  const artifactId = row.reference?.artifactId ?? row.artifactId;
  if (!artifactId) return `document:${row.documentName}`;
  const processingRevisionId = row.reference?.processingRevisionId;
  return processingRevisionId
    ? `document:${artifactId}|${processingRevisionId}`
    : `document:${artifactId}`;
}

function buildDocumentPlace(row: DocumentCitationRow): EvidencePlace {
  const unavailable = row.status === "unavailable";
  const excerpt = row.excerpt;
  const tableRows = excerpt ? parseEvidenceTableRowRange(excerpt) : null;
  return {
    key: row.key,
    // A passage that no longer resolves has no location to give, so the row says that
    // instead of leaving the reader with a name and nothing under it.
    location: unavailable ? UNAVAILABLE_DOCUMENT_EVIDENCE_TITLE : row.location,
    excerpt: excerpt !== null && tableRows ? stripEvidenceTableRowAnnotation(excerpt) : excerpt,
    monospace: row.fileKind === "code",
    table: excerpt !== null && excerptContainsTable(excerpt),
    tableRows,
    ...(row.figure ? { figure: row.figure } : {}),
    supportNote: null,
  };
}

/**
 * What one citation of a document offers on its own — the Documents row's decision, asked with
 * what a citation carries: the file's name, its type and the signed URL minted for it.
 *
 * A transcribed recording is the one source whose file is not what a reader wants: its bytes are
 * audio, and the passage is only readable in the transcript. So a row that says it is a
 * transcript opens the viewer and its `sourceUrl` — the presigned audio link — is not used.
 *
 * A source with no `fileName` has no bytes to type: a link artifact's URL is a web page, and a
 * presentation that has not resolved past the name says nothing about what it holds. Either way
 * the address is all there is, so it is offered as one.
 */
function documentRowAction(row: DocumentCitationRow): EvidenceSourceAction | null {
  if (row.status === "unavailable") return null;
  const artifactId = row.reference?.artifactId ?? row.artifactId;

  if (row.transcript) {
    return artifactId
      ? {
          kind: "transcript",
          label: VIEW_EVIDENCE_TRANSCRIPT_LABEL,
          artifactId,
          name: row.documentName,
          processingRevisionId: row.reference?.processingRevisionId ?? null,
          blockIndex: row.transcript.blockIndex ?? null,
        }
      : null;
  }

  if (!row.fileName) {
    return row.sourceUrl
      ? { kind: "link", label: OPEN_EVIDENCE_LINK_LABEL, icon: "external", href: row.sourceUrl }
      : null;
  }

  const download = (href: string): EvidenceSourceAction => ({
    kind: "link",
    label: DOWNLOAD_EVIDENCE_FILE_LABEL,
    icon: "download",
    href,
  });
  const action = resolveDocumentOpenAction({
    fileName: row.fileName,
    mimeType: row.mimeType ?? null,
    downloadUrl: row.sourceUrl,
    // Answered above, from the row's own fact.
    transcriptOffered: false,
  });
  switch (action?.kind) {
    case "preview":
      // The dialog is opened on an identified file. A citation whose URL was minted always
      // names the artifact it was minted for, so the bytes are a fallback nothing reaches.
      return artifactId
        ? {
            kind: "preview",
            label: documentPreviewMenuLabel(action.previewKind),
            previewKind: action.previewKind,
            artifactId,
            name: row.documentName,
            fileName: row.fileName,
            mimeType: row.mimeType ?? null,
            url: action.url,
          }
        : download(action.url);
    // No external source is passed, so what is left of the precedence is the bytes.
    case "source":
    case "download":
      return download(action.href);
    default:
      return null;
  }
}

/**
 * Which action wins where two citations of one file offer different ones.
 *
 * A group's rows cite one capture of one file, so the only thing that separates a preview from a
 * link is whether the URL has been minted yet — and a transcript is never traded for either.
 */
const EVIDENCE_ACTION_RANK: Record<EvidenceSourceAction["kind"], number> = {
  transcript: 3,
  preview: 2,
  link: 1,
};

function evidenceActionRank(action: EvidenceSourceAction | null): number {
  return action === null ? 0 : EVIDENCE_ACTION_RANK[action.kind];
}

function buildKnowledgeSourceGroup(row: KnowledgeSourceCitationRow): EvidenceSourceGroup {
  const place: EvidencePlace = {
    key: row.key,
    location: null,
    excerpt: row.answer,
    monospace: false,
    table: false,
    tableRows: null,
    supportNote: row.supportNote,
  };

  if (row.kind === "manual") {
    return {
      key: row.key,
      title: MANUAL_EVIDENCE_TITLE,
      icon: "manual",
      references: false,
      // A knowledge entry is already whole here; there is nowhere else to open it.
      action: null,
      meta: row.attribution,
      places: [{ ...place, excerpt: row.statement ?? null }],
    };
  }

  const location =
    row.kind === "clarification"
      ? [CLARIFICATION_SOURCE_LABEL, row.attribution].filter(Boolean).join(" · ")
      : row.attribution;

  return {
    key: row.key,
    title: row.heading ?? CLARIFICATION_SOURCE_LABEL,
    icon: "message",
    references: true,
    action: row.href
      ? { kind: "link", label: OPEN_EVIDENCE_REQUEST_LABEL, icon: "external", href: row.href }
      : null,
    meta: null,
    places: [{ ...place, location }],
  };
}

/**
 * Collapses citation rows into the sources the popover draws, in the order the sources are
 * first cited. Grouping is what removes the repetition the reader would otherwise have to
 * skim past: the file name and its action are stated once, and the places under it are only
 * ever "where" and "what".
 */
export function groupEvidenceRows(
  rows: readonly EvidenceCitationRow[]
): readonly EvidenceSourceGroup[] {
  const groups: EvidenceSourceGroup[] = [];
  const byKey = new Map<string, EvidenceSourceGroup>();

  for (const row of rows) {
    if (row.kind !== "document") {
      groups.push(buildKnowledgeSourceGroup(row));
      continue;
    }
    const key = documentGroupKey(row);
    const existing = byKey.get(key);
    if (existing) {
      existing.places.push(buildDocumentPlace(row));
      // A group that started on an unresolvable citation has no action; a later citation of
      // the same document that did resolve is what gives the whole group one. Only a
      // higher-ranked action replaces what the group already has, so a transcript keeps the
      // turn it landed on — a recording's audio is never the way to read a quoted passage —
      // and a preview is never traded back for the link that stood in for it.
      const action = documentRowAction(row);
      if (evidenceActionRank(action) > evidenceActionRank(existing.action)) {
        existing.action = action;
      }
      if (row.sourceOutdated) existing.sourceOutdated = true;
      continue;
    }
    const group: EvidenceSourceGroup = {
      key,
      title: row.documentName,
      icon: evidenceFileIconKey(row.fileKind),
      references: false,
      action: documentRowAction(row),
      meta: null,
      ...(row.sourceOutdated ? { sourceOutdated: true as const } : {}),
      places: [buildDocumentPlace(row)],
    };
    byKey.set(key, group);
    groups.push(group);
  }

  return groups;
}
